import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { useState, useRef } from 'react'
import { users } from '@/mocks'
import { Avatar } from '../../common'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 24, maxWidth: 600 })

const ProfileCard = styled(View, {
  backgroundColor: '$surfaceRaised', borderRadius: '$borderRadiusXl', borderWidth: 1, borderColor: '$borderDefault',
  overflow: 'hidden',
})

const ProfileBanner = styled(View, {
  height: 80,
})

const ProfileInfo = styled(View, {
  padding: 16, paddingTop: 0, flexDirection: 'row', alignItems: 'flex-end', gap: 16,
  marginTop: -28,
})

const ProfileDetails = styled(View, { flex: 1, gap: 2, paddingBottom: 4 })

const FormGroup = styled(View, { gap: 6 })

const Label = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, fontWeight: '600',
  color: '$color8', textTransform: 'uppercase', letterSpacing: 0.5,
})

const SaveButton = styled(View, {
  height: 36, paddingHorizontal: 24, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center', backgroundColor: '$accent',
  cursor: 'pointer', alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const DangerZone = styled(View, {
  borderWidth: 1, borderColor: '$error', borderRadius: '$borderRadiusLg', padding: 16, gap: 8,
})

const DangerButton = styled(View, {
  height: 36, paddingHorizontal: 16, borderRadius: '$borderRadiusMd',
  alignItems: 'center', justifyContent: 'center',
  borderWidth: 1, borderColor: '$error', cursor: 'pointer',
  hoverStyle: { backgroundColor: 'rgba(207, 62, 79, 0.1)' },
})

const AvatarWrapper = styled(View, {
  position: 'relative',
  cursor: 'pointer',
})

const AvatarOverlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '$borderRadiusFull',
  backgroundColor: 'rgba(0,0,0,0.5)',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  hoverStyle: { opacity: 1 },
})

export function AccountSettings() {
  const user = users['u1']
  const theme = useTheme()
  const [displayName, setDisplayName] = useState(user.displayName)
  const [bio, setBio] = useState(user.bio || '')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const url = URL.createObjectURL(file)
      setAvatarPreview(url)
    }
  }

  return (
    <Container>
      <Header><Title>My Account</Title></Header>
      <ScrollView flex={1}>
        <Body>
          <ProfileCard>
            <ProfileBanner backgroundColor={user.roleColor || theme.accent?.val || '#3ECFCF'} />
            <ProfileInfo>
              <View borderWidth={4} borderColor="$surfaceRaised" borderRadius="$borderRadiusFull">
                <AvatarWrapper onPress={handleAvatarClick}>
                  {avatarPreview ? (
                    <View width={56} height={56} borderRadius="$borderRadiusFull" overflow="hidden">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      />
                    </View>
                  ) : (
                    <Avatar name={user.displayName} color={user.roleColor || theme.accent?.val || '#3ECFCF'} status={user.status} size="xl" />
                  )}
                  <AvatarOverlay>
                    <Text fontFamily="$fontBody" fontSize={10} fontWeight="600" color="white" textAlign="center">
                      Change{'\n'}Avatar
                    </Text>
                  </AvatarOverlay>
                </AvatarWrapper>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </View>
              <ProfileDetails>
                <Text fontFamily="$fontHeading" fontWeight="600" fontSize={18} color="$surfaceForeground">
                  {user.displayName}
                </Text>
                <Text fontFamily="$fontMono" fontSize={12} color="$color8">
                  @{user.username}#{user.discriminator}
                </Text>
              </ProfileDetails>
            </ProfileInfo>
          </ProfileCard>

          <FormGroup>
            <Label>Display Name</Label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={{
              background: 'transparent', border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
              borderRadius: 6, padding: '8px 12px', color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: '$fontBody', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box',
            }} />
          </FormGroup>

          <FormGroup>
            <Label>Bio</Label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell others about yourself" rows={3} style={{
              background: 'transparent', border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
              borderRadius: 6, padding: '8px 12px', color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: '$fontBody', fontSize: 14, outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box',
            }} />
          </FormGroup>

          <SaveButton>
            <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Save Changes</Text>
          </SaveButton>

          <DangerZone>
            <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$error">Danger Zone</Text>
            <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
              Deleting your account is permanent. All your data will be erased.
            </Text>
            <DangerButton>
              <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$error">Delete Account</Text>
            </DangerButton>
          </DangerZone>
        </Body>
      </ScrollView>
    </Container>
  )
}
